import React, { useEffect, useState } from 'react';
import { Mail, Loader2, Check, AlertCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { api, apiError } from '@/lib/api';

interface NewsCfg {
  enabled: boolean;
  kicker: string;
  heading: string;
  subheading: string;
  button_label: string;
  success_message: string;
  show_name_field: boolean;
}

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const DONE_KEY = 'ek-newsletter-joined';

export default function NewsletterSection() {
  const [cfg, setCfg] = useState<NewsCfg | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [done, setDone] = useState<string | null>(() => {
    try {
      return localStorage.getItem(DONE_KEY);
    } catch {
      return null;
    }
  });

  useEffect(() => {
    api.get<NewsCfg>('/public/newsletter').then((r) => setCfg(r.data)).catch(() => setCfg(null));
  }, []);

  if (!cfg || !cfg.enabled) return null;

  const emailOk = EMAIL_RE.test(email.trim());

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!emailOk || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ status: string }>('/public/newsletter/subscribe', {
        email: email.trim(),
        name: name.trim(),
        source: 'landing',
        website,
      });
      const addr = email.trim().toLowerCase();
      try {
        localStorage.setItem(DONE_KEY, addr);
      } catch {
        /* private mode */
      }
      setDone(addr);
      if (r.data.status === 'already') {
        toast('You’re already on the list', { description: `${addr} is subscribed — see you in your inbox.` });
      } else {
        toast.success('Subscribed', { description: cfg.success_message || 'Welcome aboard!' });
      }
      setEmail('');
      setName('');
      setTouched(false);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="newsletter" className="ek-news" aria-labelledby="newsletter-title" data-testid="newsletter-section">
      <div className="ek-news-inner">
        <div className="ek-news-copy">
          {cfg.kicker && <p className="ek-news-kicker">{cfg.kicker}</p>}
          <h2 id="newsletter-title" className="ek-news-title" data-testid="newsletter-heading">{cfg.heading}</h2>
          {cfg.subheading && <p className="ek-news-lede">{cfg.subheading}</p>}
        </div>

        {done ? (
          <div className="ek-news-done" role="status" data-testid="newsletter-success">
            <span className="ek-news-done-ico" aria-hidden="true"><Check size={18} /></span>
            <div>
              <p className="ek-news-done-title">{cfg.success_message || 'You’re subscribed!'}</p>
              <p className="ek-news-done-sub">Subscribed as <strong>{done}</strong></p>
              <button
                type="button"
                className="ek-news-again"
                onClick={() => {
                  setDone(null);
                  try { localStorage.removeItem(DONE_KEY); } catch { /* ignore */ }
                }}
                data-testid="newsletter-subscribe-another"
              >
                Subscribe another email
              </button>
            </div>
          </div>
        ) : (
          <form className={`ek-news-form ${cfg.show_name_field ? 'has-name' : ''}`} onSubmit={submit} noValidate data-testid="newsletter-form">
            {cfg.show_name_field && (
              <label className="ek-news-field">
                <span className="sr-only">First name (optional)</span>
                <input
                  type="text"
                  className="ek-news-input"
                  autoComplete="given-name"
                  placeholder="First name"
                  value={name}
                  maxLength={120}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="newsletter-name-input"
                />
              </label>
            )}
            <label className="ek-news-field is-email">
              <span className="sr-only">Email address</span>
              <Mail size={15} className="ek-news-ico" aria-hidden="true" />
              <input
                type="email"
                className={`ek-news-input has-ico ${touched && !emailOk ? 'is-invalid' : ''}`}
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                maxLength={254}
                required
                aria-invalid={touched && !emailOk}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email-input"
              />
            </label>
            <label className="ek-contact-honey" aria-hidden="true">
              Website
              <input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
            </label>
            <button type="submit" className="ek-news-btn" disabled={busy} data-testid="newsletter-submit-button">
              {busy ? <Loader2 size={15} className="ek-spin" /> : null}
              {busy ? 'Joining…' : cfg.button_label || 'Subscribe'}
              {!busy && <ArrowRight size={15} />}
            </button>
            {(error || (touched && !emailOk)) && (
              <p className="ek-news-error" role="alert" data-testid="newsletter-error">
                <AlertCircle size={13} /> {error || 'Please enter a valid email address.'}
              </p>
            )}
            <p className="ek-news-note">
              One-click unsubscribe in every email. Read our <a href="/legal/privacy">privacy policy</a>.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
